/**
 * useSyncedState — a useState drop-in whose value is SHARED with the other person in a
 * co-driven demo. Either screen can change it, and both screens show the result.
 *
 *   • A local set updates this screen and broadcasts `demo_ui {key, value, stamp}`
 *     (paced to one frame per key per 50ms, always ending on the latest value).
 *   • The other screen keeps it only if it is newer than what it holds (sync-core: last
 *     writer wins), so two changes crossing on the wire still leave both screens
 *     identical — and applying a change never re-broadcasts it (the no-op damper).
 *   • Late join: the host pushes `demo_ui_snapshot` (every value + stamp). It is
 *     authoritative, and a guest broadcasts nothing until it lands, so a guest's
 *     pre-sync defaults can never overwrite the host's live state.
 *
 * Outside a live room (the static build, and solo visitors on the live build) this is a
 * plain useState: no shared store, no network.
 */
import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import { getSocket, isRelayActive } from "../../shims/socket-client";
import { LIVE, type LiveRole } from "./live";
import { HybridClock, LwwMap, isStamp } from "./sync-core";

/** Per-tab writer id: the deterministic tie-break for two writes stamped the same ms. */
const TAB_ID = (() => {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
})();

const clock = new HybridClock();
const ui = new LwwMap(clock, TAB_ID);
/** The shared route, as a one-key register (read and written by LiveSession). */
export const navRegister = new LwwMap(clock, TAB_ID);

type Listener = (value: unknown, reset?: boolean) => void;
const listeners = new Map<string, Set<Listener>>();
function notify(key: string, value: unknown, reset?: boolean): void {
  listeners.get(key)?.forEach((fn) => fn(value, reset));
}

// ── Room lifecycle ─────────────────────────────────────────────────────────────
let room: { code: string; role: LiveRole } | null = null;
let synced = false; // host: from the start · guest: once the host's snapshot lands

// Per-key send pacing. A slider drag fires an input event per display frame (120/s on
// ProMotion screens) and the relay drops anything over 120 events/s per socket — which
// could lose a drag's FINAL value and leave the two screens disagreeing.
const SEND_GAP_MS = 50;
const lastSentAt = new Map<string, number>();
const pendingSends = new Map<string, ReturnType<typeof setTimeout>>();

function resetShared(): void {
  ui.clear();
  navRegister.clear();
  for (const t of pendingSends.values()) clearTimeout(t);
  pendingSends.clear();
  lastSentAt.clear();
  synced = false;
}

/** A co-driven room started (LiveRoomProvider). Fresh shared state for every room. */
export function enterSyncRoom(code: string, role: LiveRole): void {
  if (room && room.code === code && room.role === role) return; // re-entering the same room
  resetShared();
  room = { code, role };
  synced = role === "host";
  wire();
}

/** The room ended — back to plain local state. */
export function leaveSyncRoom(): void {
  resetShared();
  room = null;
}

function inRoom(): boolean {
  return LIVE && room !== null && isRelayActive();
}

/** Whether this screen may broadcast shared changes yet (a guest waits for the host's snapshot). */
export function canBroadcast(): boolean {
  return inRoom() && synced;
}

// ── Wire protocol ──────────────────────────────────────────────────────────────
function sendNow(key: string): void {
  const stamp = ui.stampOf(key);
  // Only our own latest write goes out. If the other screen's newer change replaced it
  // while this send was pending, theirs stands and there is nothing to send.
  if (!stamp || stamp.by !== TAB_ID || !canBroadcast()) return;
  lastSentAt.set(key, Date.now());
  getSocket().emit("demo_ui", { key, value: ui.get(key), stamp });
}

function scheduleSend(key: string): void {
  if (pendingSends.has(key)) return; // the pending send picks up the latest value
  const wait = SEND_GAP_MS - (Date.now() - (lastSentAt.get(key) ?? 0));
  if (wait <= 0) {
    sendNow(key);
    return;
  }
  pendingSends.set(
    key,
    setTimeout(() => {
      pendingSends.delete(key);
      sendNow(key);
    }, wait),
  );
}

let wired = false;
function wire(): void {
  if (wired || !LIVE) return;
  wired = true;
  const s = getSocket();
  s.on("demo_ui", (p: { key?: unknown; value?: unknown; stamp?: unknown }) => {
    if (!inRoom() || !p || typeof p.key !== "string") return;
    if (ui.applyRemote(p.key, p.value, isStamp(p.stamp) ? p.stamp : undefined)) notify(p.key, p.value);
  });
  s.on("demo_ui_snapshot", (p: { entries?: unknown }) => {
    // Only a guest adopts a snapshot (only the host sends one).
    if (!inRoom() || room?.role !== "guest" || !p || !Array.isArray(p.entries)) return;
    const incoming = new Set<string>();
    for (const e of p.entries) {
      if (!Array.isArray(e) || typeof e[0] !== "string") continue;
      const [k, v, st] = e as [string, unknown, unknown];
      incoming.add(k);
      if (ui.applyRemote(k, v, isStamp(st) ? st : undefined, true)) notify(k, v);
    }
    // Authoritative: anything this screen changed before syncing that the host doesn't
    // hold reverts to its default (e.g. a tour opened on the waiting screen).
    for (const [k] of ui.snapshot()) {
      if (!incoming.has(k)) {
        ui.delete(k);
        notify(k, undefined, true);
      }
    }
    synced = true;
  });
}

/** Current shared UI state + stamps, for the host's late-join snapshot (LiveSession). */
export function snapshotSyncedState(): [string, unknown, unknown][] {
  return ui.snapshot();
}

export function useSyncedState<T>(key: string, initial: T): [T, (v: SetStateAction<T>) => void] {
  const [value, setLocal] = useState<T>(() => (inRoom() && ui.has(key) ? (ui.get(key) as T) : initial));
  // Latest value + first-render default, readable from the stable setter/listener.
  const valueRef = useRef(value);
  valueRef.current = value;
  const initialRef = useRef(initial);

  useEffect(() => {
    if (!LIVE) return;
    wire();
    // Adopt a value that changed while this hook wasn't mounted (a late-join snapshot,
    // or the other person changed it while this screen was on another page).
    if (inRoom() && ui.has(key)) setLocal(ui.get(key) as T);
    const fn: Listener = (v, reset) => setLocal(reset ? initialRef.current : (v as T));
    let set = listeners.get(key);
    if (!set) listeners.set(key, (set = new Set()));
    set.add(fn);
    return () => {
      set!.delete(fn);
    };
  }, [key]);

  const setValue = useCallback(
    (v: SetStateAction<T>) => {
      if (!inRoom()) {
        setLocal(v); // plain useState outside a live room
        return;
      }
      const prev = ui.has(key) ? (ui.get(key) as T) : valueRef.current;
      const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
      setLocal(next);
      // null = nothing actually changed (e.g. re-writing the value the other person just
      // set), so nothing is sent — this is what keeps the two screens from echoing.
      if (ui.setLocal(key, next, valueRef.current)) {
        notify(key, next); // keep any other hook on this key, on this screen, in step
        scheduleSend(key);
      }
    },
    [key],
  );

  return [value, setValue];
}
